import { Router } from '@angular/router';
import { NeoTopNavbarSubNavItem } from '@neogov/top-navbar-definition';
import { NeoTopNavbarAnchorClickableHelper } from '@neogov/unified-suite-library/services';
import { NavBarProvider } from '../navbar.provider';
import { NavItemSource } from './nav-item-source.model';

/**
 * Navigation item model for the top navbar.
 * Extends NeoTopNavbarSubNavItem with factory methods for creating instances.
 *
 * @example
 * ```typescript
 * // Create from raw data
 * const navItem = NavBarNavigationItem.create({
 *   id: 'cases-dashboard',
 *   title: 'Dashboard',
 *   clickable: {
 *     type: 'anchor',
 *     anchorHref: '/cases/dashboard',
 *     onClick: (e) => {
 *       this.router.navigate(['/cases/dashboard']);
 *       return { preventOverlayClose: false };
 *     }
 *   },
 *   items: []
 * });
 *
 * // Create from NavItemSource
 * const navItem = NavBarNavigationItem.createFromNavItemSource(
 *   source,
 *   router,
 *   navBarProvider
 * );
 * ```
 */
export class NavBarNavigationItem implements NeoTopNavbarSubNavItem {
  public readonly id!: string;
  public readonly title!: string;
  public readonly clickable!: NeoTopNavbarSubNavItem['clickable'];
  public readonly items!: NeoTopNavbarSubNavItem['items'];

  private constructor(data: NeoTopNavbarSubNavItem) {
    Object.assign(this, data);
  }

  /**
   * Creates a new NavBarNavigationItem instance from raw data.
   *
   * @param data - The navigation item data conforming to NeoTopNavbarSubNavItem
   * @returns A new NavBarNavigationItem instance
   */
  public static create(data: NeoTopNavbarSubNavItem): NavBarNavigationItem {
    return new NavBarNavigationItem(data);
  }

  /**
   * Creates a new NavBarNavigationItem from a NavItemSource.
   * Transforms the source into a fully configured navigation item with click handlers.
   *
   * @param source - The navigation item source data
   * @param router - Angular Router for navigation
   * @param navBarProvider - NavBarProvider for setting selected navigation ID
   * @returns A new NavBarNavigationItem instance
   *
   * @example
   * ```typescript
   * const source: NavItemSource = {
   *   id: 'cases-dashboard',
   *   title: 'Dashboard',
   *   anchorHref: '/cases/dashboard',
   *   requiredPermission: CasePermission.CaseManagementViewOpen
   * };
   *
   * const navItem = NavBarNavigationItem.createFromNavItemSource(
   *   source,
   *   router,
   *   navBarProvider
   * );
   * ```
   */
  public static createFromNavItemSource(
    source: NavItemSource,
    router: Router,
    navBarProvider: NavBarProvider
  ): NavBarNavigationItem {
    return new NavBarNavigationItem({
      id: source.id,
      title: source.title,
      clickable: {
        type: 'button' as const,
        onClick: (e: Event) => {
          navBarProvider.setSelectedNavigationId(source.id);
          if (NeoTopNavbarAnchorClickableHelper.allowEventToGoThrough(e)) {
            return { preventOverlayClose: true };
          }
          router.navigate([source.anchorHref]);
          return { preventOverlayClose: false };
        },
      },
      items: [],
    });
  }
}
